import { supabase } from '../lib/supabase';

// ─── SCHEMA REAL ──────────────────────────────────────────────────────────────
// public.conversaciones:
//   id, cliente_id, canal, estado, departamento, intencion_actual,
//   responsable_id, modo, prioridad, ultimo_mensaje_at, requiere_atencion_desde,
//   cerrada_at, metadata, created_at, updated_at
//
// public.mensajes:
//   id, conversacion_id, mensaje_externo_id, direccion (entrante|saliente),
//   autor_tipo (cliente|ia|humano|sistema), contenido, tipo_contenido,
//   media_url, transcripcion, estado, enviado_at, recibido_at, leido_at,
//   metadata, created_at
//
// public.conversacion_eventos:
//   id, conversacion_id, mensaje_id, tipo_evento, intencion, departamento,
//   confianza, datos_extraidos, requiere_confirmacion, confirmado, created_at
//
// public.alertas_atencion:
//   id, conversacion_id, cliente_id, nivel, motivo, resumen_ia, estado,
//   asignado_a, escalada_at, atendida_at, resuelta_at, metadata, created_at, updated_at
//
// public.comercial_sla_config:
//   id, canal, minutos_atencion, minutos_urgente,
//   notificacion_erp, notificacion_push, notificacion_whatsapp, notificacion_email,
//   notificacion_llamada, activo, created_at, updated_at
// ─────────────────────────────────────────────────────────────────────────────

// ── CONVERSACIONES ────────────────────────────────────────────────────────────

export async function getConversaciones({ canal, estado } = {}) {
  let query = supabase
    ?.from('conversaciones')
    ?.select(`
      id,
      canal,
      estado,
      departamento,
      intencion_actual,
      modo,
      prioridad,
      ultimo_mensaje_at,
      requiere_atencion_desde,
      cerrada_at,
      created_at,
      updated_at,
      cliente_id,
      metadata,
      clientes (
        id,
        nombre,
        nombre_comercial,
        telefono,
        email
      )
    `)
    ?.order('ultimo_mensaje_at', { ascending: false, nullsFirst: false });

  if (canal && canal !== 'todos' && canal !== 'no_leidos' && canal !== 'atencion' && canal !== 'pedidos') {
    query = query?.eq('canal', canal);
  }
  if (estado) {
    query = query?.eq('estado', estado);
  }
  if (canal === 'atencion') {
    query = query?.in('estado', ['requiere_atencion', 'urgente']);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[ComercialService] getConversaciones error:', error);
    throw error;
  }
  return data || [];
}

export async function getConversacionById(id) {
  const { data, error } = await supabase
    ?.from('conversaciones')
    ?.select(`
      id,
      canal,
      estado,
      departamento,
      intencion_actual,
      modo,
      prioridad,
      ultimo_mensaje_at,
      requiere_atencion_desde,
      cerrada_at,
      created_at,
      updated_at,
      cliente_id,
      metadata,
      clientes (
        id,
        nombre,
        nombre_comercial,
        telefono,
        email
      )
    `)
    ?.eq('id', id)
    ?.single();

  if (error) {
    console.error('[ComercialService] getConversacionById error:', error);
    throw error;
  }
  return data;
}

export async function getMensajesByConversacion(conversacionId) {
  const { data, error } = await supabase
    ?.from('mensajes')
    ?.select(`
      id,
      conversacion_id,
      direccion,
      autor_tipo,
      contenido,
      tipo_contenido,
      media_url,
      transcripcion,
      estado,
      enviado_at,
      recibido_at,
      leido_at,
      created_at
    `)
    ?.eq('conversacion_id', conversacionId)
    ?.order('created_at', { ascending: true });

  if (error) {
    console.error('[ComercialService] getMensajesByConversacion error:', error);
    throw error;
  }
  return data || [];
}

// Marcar mensajes entrantes como leídos al abrir conversación
export async function markMensajesLeidos(conversacionId) {
  const { error } = await supabase
    ?.from('mensajes')
    ?.update({ leido_at: new Date()?.toISOString() })
    ?.eq('conversacion_id', conversacionId)
    ?.eq('direccion', 'entrante')
    ?.is('leido_at', null);

  if (error) {
    console.error('[ComercialService] markMensajesLeidos error:', error);
    // No lanzar error — no es crítico
  }
}

// Obtener conteo de no leídos por conversación
export async function getUnreadCountByConversacion(conversacionId) {
  const { count, error } = await supabase
    ?.from('mensajes')
    ?.select('id', { count: 'exact', head: true })
    ?.eq('conversacion_id', conversacionId)
    ?.eq('direccion', 'entrante')
    ?.is('leido_at', null);

  if (error) {
    console.error('[ComercialService] getUnreadCountByConversacion error:', error);
    return 0;
  }
  return count || 0;
}

// Obtener último mensaje de una conversación (para previsualización en bandeja)
export async function getUltimoMensaje(conversacionId) {
  const { data, error } = await supabase
    ?.from('mensajes')
    ?.select('id, contenido, tipo_contenido, direccion, created_at')
    ?.eq('conversacion_id', conversacionId)
    ?.order('created_at', { ascending: false })
    ?.limit(1)
    ?.single();

  if (error && error?.code !== 'PGRST116') {
    console.error('[ComercialService] getUltimoMensaje error:', error);
  }
  return data || null;
}

export async function getEventosByConversacion(conversacionId) {
  const { data, error } = await supabase
    ?.from('conversacion_eventos')
    ?.select(`
      id,
      conversacion_id,
      mensaje_id,
      tipo_evento,
      intencion,
      departamento,
      confianza,
      datos_extraidos,
      requiere_confirmacion,
      confirmado,
      created_at
    `)
    ?.eq('conversacion_id', conversacionId)
    ?.order('created_at', { ascending: true });

  if (error) {
    console.error('[ComercialService] getEventosByConversacion error:', error);
    throw error;
  }
  return data || [];
}

// ── ACCIONES SOBRE CONVERSACIÓN ───────────────────────────────────────────────

export async function tomarConversacion(conversacionId) {
  const { error } = await supabase
    ?.from('conversaciones')
    ?.update({
      modo: 'humano',
      estado: 'modo_humano',
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', conversacionId);

  if (error) {
    console.error('[ComercialService] tomarConversacion error:', error);
    throw error;
  }
}

export async function devolverAIA(conversacionId) {
  const { error } = await supabase
    ?.from('conversaciones')
    ?.update({
      modo: 'ia',
      estado: 'automatico',
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', conversacionId);

  if (error) {
    console.error('[ComercialService] devolverAIA error:', error);
    throw error;
  }
}

export async function reabrirConversacion(conversacionId) {
  const { error } = await supabase
    ?.from('conversaciones')
    ?.update({
      modo: 'humano',
      estado: 'modo_humano',
      cerrada_at: null,
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', conversacionId);

  if (error) {
    console.error('[ComercialService] reabrirConversacion error:', error);
    throw error;
  }
}

export async function vincularConversacionCliente(conversacion, clienteId) {
  if (!conversacion?.id || !clienteId) throw new Error('Faltan datos para vincular el cliente');

  const { error: convError } = await supabase
    ?.from('conversaciones')
    ?.update({ cliente_id: clienteId, updated_at: new Date()?.toISOString() })
    ?.eq('id', conversacion.id);

  if (convError) throw convError;

  const canal = conversacion?.canal;
  const identificador = conversacion?.identificador_hilo_externo;
  if (['whatsapp', 'instagram', 'facebook', 'email'].includes(canal) && identificador) {
    const nombreUsuario =
      canal === 'instagram'
        ? (conversacion?.metadata?.instagram_username || conversacion?.metadata?.instagram_name || null)
        : canal === 'whatsapp'
          ? (conversacion?.metadata?.nombre_whatsapp || null)
          : null;

    const { data: existente } = await supabase
      ?.from('cliente_canales')
      ?.select('id,cliente_id')
      ?.eq('canal', canal)
      ?.eq('identificador_externo', identificador)
      ?.maybeSingle();

    if (existente?.id && existente?.cliente_id !== clienteId) {
      throw new Error('Este canal ya está vinculado a otro cliente');
    }

    if (!existente?.id) {
      const { error: canalError } = await supabase
        ?.from('cliente_canales')
        ?.insert({
          cliente_id: clienteId,
          canal,
          identificador_externo: identificador,
          nombre_usuario: nombreUsuario,
          principal: false,
          verificado: true,
          activo: true,
          metadata: { origen: 'vinculacion_manual_comercial' },
        });
      if (canalError) throw canalError;
    } else {
      const { error: canalError } = await supabase
        ?.from('cliente_canales')
        ?.update({ activo: true, nombre_usuario: nombreUsuario || undefined, updated_at: new Date()?.toISOString() })
        ?.eq('id', existente.id);
      if (canalError) throw canalError;
    }
  }
}

export async function cerrarConversacion(conversacionId) {
  const { error } = await supabase
    ?.from('conversaciones')
    ?.update({
      estado: 'cerrado',
      cerrada_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', conversacionId);

  if (error) {
    console.error('[ComercialService] cerrarConversacion error:', error);
    throw error;
  }
}

// ── CONTADORES PARA ATENCIÓN ──────────────────────────────────────────────────

export async function getContadoresEstado() {
  const { data, error } = await supabase
    ?.from('conversaciones')
    ?.select('estado');

  if (error) {
    console.error('[ComercialService] getContadoresEstado error:', error);
    throw error;
  }

  const contadores = {
    automatico: 0,
    requiere_atencion: 0,
    urgente: 0,
    modo_humano: 0,
    cerrado: 0,
  };

  (data || [])?.forEach(c => {
    if (contadores?.hasOwnProperty(c?.estado)) {
      contadores[c?.estado]++;
    }
  });

  return contadores;
}

// ── ALERTAS ───────────────────────────────────────────────────────────────────

export async function getAlertasActivas() {
  const { data, error } = await supabase
    ?.from('alertas_atencion')
    ?.select(`
      id,
      conversacion_id,
      cliente_id,
      nivel,
      motivo,
      resumen_ia,
      estado,
      escalada_at,
      atendida_at,
      created_at,
      updated_at,
      clientes (
        id,
        nombre,
        nombre_comercial
      ),
      conversaciones (
        id,
        canal,
        estado,
        modo
      )
    `)
    ?.in('estado', ['abierta', 'atendida'])
    ?.order('nivel', { ascending: false })
    ?.order('created_at', { ascending: true });

  if (error) {
    console.error('[ComercialService] getAlertasActivas error:', error);
    throw error;
  }
  return data || [];
}

export async function atenderAlerta(alertaId, conversacionId) {
  const { error: alertaError } = await supabase
    ?.from('alertas_atencion')
    ?.update({
      estado: 'atendida',
      atendida_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', alertaId);

  if (alertaError) {
    console.error('[ComercialService] atenderAlerta alertas error:', alertaError);
    throw alertaError;
  }

  const { error: convError } = await supabase
    ?.from('conversaciones')
    ?.update({
      modo: 'humano',
      estado: 'modo_humano',
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', conversacionId);

  if (convError) {
    console.error('[ComercialService] atenderAlerta conversacion error:', convError);
    throw convError;
  }
}

export async function resolverAlerta(alertaId) {
  const { error } = await supabase
    ?.from('alertas_atencion')
    ?.update({
      estado: 'resuelta',
      resuelta_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', alertaId);

  if (error) {
    console.error('[ComercialService] resolverAlerta error:', error);
    throw error;
  }
}

// ── SLA CONFIG ────────────────────────────────────────────────────────────────

export async function getSlaConfig() {
  const { data, error } = await supabase
    ?.from('comercial_sla_config')
    ?.select(`
      id,
      canal,
      minutos_atencion,
      minutos_urgente,
      notificacion_erp,
      notificacion_push,
      notificacion_whatsapp,
      notificacion_email,
      notificacion_llamada,
      activo,
      updated_at
    `)
    ?.eq('canal', 'todos')
    ?.single();

  if (error) {
    console.error('[ComercialService] getSlaConfig error:', error);
    throw error;
  }
  return data;
}

export async function updateSlaConfig(id, { minutos_atencion, minutos_urgente, notificacion_erp, notificacion_push, notificacion_whatsapp, notificacion_email, notificacion_llamada }) {
  const { error } = await supabase
    ?.from('comercial_sla_config')
    ?.update({
      minutos_atencion,
      minutos_urgente,
      notificacion_erp,
      notificacion_push,
      notificacion_whatsapp,
      notificacion_email,
      notificacion_llamada,
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', id);

  if (error) {
    console.error('[ComercialService] updateSlaConfig error:', error);
    throw error;
  }
}


// ── ENVÍO MANUAL WHATSAPP ────────────────────────────────────────────────────

export async function enviarWhatsAppManual(conversacionId, mensaje) {
  const texto = mensaje?.trim();

  if (!conversacionId) {
    throw new Error('Falta la conversación');
  }
  if (!texto) {
    throw new Error('Escribe un mensaje antes de enviar');
  }

  const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
  if (sessionError) {
    console.error('[ComercialService] enviarWhatsAppManual session error:', sessionError);
    throw sessionError;
  }

  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) {
    throw new Error('La sesión ha caducado. Vuelve a iniciar sesión.');
  }

  const { data, error } = await supabase?.functions?.invoke('whatsapp-send', {
    body: {
      conversacion_id: conversacionId,
      mensaje: texto,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (error) {
    console.error('[ComercialService] enviarWhatsAppManual invoke error:', error);
    throw new Error(error?.message || 'No se pudo enviar el WhatsApp');
  }

  if (!data?.ok) {
    throw new Error(data?.error || 'WhatsApp no confirmó el envío');
  }

  return data;
}


// ── ENVÍO MANUAL INSTAGRAM ──────────────────────────────────────────────────

export async function enviarInstagramManual(conversacionId, mensaje) {
  const texto = mensaje?.trim();
  if (!conversacionId) throw new Error('Falta la conversación');
  if (!texto) throw new Error('Escribe un mensaje antes de enviar');

  const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
  if (sessionError) throw sessionError;
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('La sesión ha caducado. Vuelve a iniciar sesión.');

  const { data, error } = await supabase?.functions?.invoke('instagram-send', {
    body: { conversacion_id: conversacionId, mensaje: texto },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) throw new Error(error?.message || 'No se pudo enviar el mensaje de Instagram');
  if (!data?.ok) throw new Error(data?.error || 'Instagram no confirmó el envío');
  return data;
}


export async function enviarEmailManual(conversacionId, mensaje) {
  const texto = mensaje?.trim();
  if (!conversacionId) throw new Error('Falta la conversación');
  if (!texto) throw new Error('Escribe un mensaje antes de enviar');
  const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
  if (sessionError) throw sessionError;
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('La sesión ha caducado. Vuelve a iniciar sesión.');
  const { data, error } = await supabase?.functions?.invoke('email-send', {
    body: { conversacion_id: conversacionId, mensaje: texto },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) throw new Error(error?.message || 'No se pudo enviar el email');
  if (!data?.ok) throw new Error(data?.error || 'Gmail no confirmó el envío');
  return data;
}


// ── IA COMERCIAL EN MODO PRUEBA ─────────────────────────────────────────────

export async function analizarConversacionIA(conversacionId) {
  if (!conversacionId) throw new Error('Falta la conversación');

  const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
  if (sessionError) throw sessionError;
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('La sesión ha caducado. Vuelve a iniciar sesión.');

  const { data, error } = await supabase?.functions?.invoke('commercial-ai-test', {
    body: { conversacion_id: conversacionId },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) throw new Error(error?.message || 'No se pudo analizar la conversación');
  if (!data?.ok) throw new Error(data?.error || 'La IA no pudo analizar la conversación');
  return data;
}
